import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore - Leaflet runtime import is already used by Roddo Big Blue.
import L from 'leaflet';
import type { Tournament } from '../../types';
import { forwardGeocodeServer } from '../../services/geocodeClient';
import { resolveRoddoDarkMode } from '../../constants/theme';

type TournamentMapLocation = NonNullable<Tournament['mapLocation']>;

type Props = {
  locationLabel: string;
  value?: Tournament['mapLocation'];
  onChange: (value: TournamentMapLocation | null) => void;
  addToast: (message: string, type?: any) => void;
};

const FALLBACK_CENTER = { lat: 34.0522, lng: -118.2437 };

function validLatLng(value: any) {
  const lat = Number(value?.lat);
  const lng = Number(value?.lng);
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
    ? { lat, lng }
    : null;
}

function createTournamentPinIcon(_confirmed: boolean, dark = false) {
  return (L as any).divIcon({
    className: 'roddo-tournament-location-picker-icon',
    iconSize: [52, 62],
    iconAnchor: [26, 58],
    popupAnchor: [0, -48],
    html: `
      <div style="position:relative;width:52px;height:62px;overflow:visible;filter:drop-shadow(0 4px 7px rgba(15,23,42,.16));">
        <div style="position:absolute;left:50%;top:18px;width:58px;height:58px;border-radius:999px;transform:translate(-50%,-50%);background:radial-gradient(circle,rgba(246,190,50,.28) 0%,rgba(255,255,255,0) 68%);"></div>
        <div style="position:absolute;left:50%;top:4px;width:48px;height:48px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,#FFD45F 0%,#C9830D 84%);border:2px solid rgba(255,255,255,.98);box-shadow:0 7px 16px rgba(95,59,8,.24),0 0 0 4px rgba(246,190,50,.24);">
          <div style="position:absolute;inset:5px;border-radius:999px;background:${dark ? 'linear-gradient(145deg,#1A2438 0%,#0F1728 100%)' : 'linear-gradient(145deg,#243552 0%,#142238 100%)'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,.10),0 2px 5px rgba(15,23,42,.20);">
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width:22px;height:22px;display:block;overflow:visible;">
                <path d="M8 4.75H16V8.5C16 10.709 14.209 12.5 12 12.5C9.791 12.5 8 10.709 8 8.5V4.75Z" fill="rgba(255,215,106,.18)" stroke="#FFD76A" stroke-width="1.8" stroke-linejoin="round"/>
                <path d="M8 6H5.75C5.336 6 5 6.336 5 6.75V7.5C5 9.433 6.567 11 8.5 11M16 6H18.25C18.664 6 19 6.336 19 6.75V7.5C19 9.433 17.433 11 15.5 11" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 12.5V16M9.5 16H14.5M9 19H15" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
          </div>
        </div>
      </div>
    `,
  });
}

export default function TournamentLocationPicker({ locationLabel, value, onChange, addToast }: Props) {
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const initial = validLatLng(value) || FALLBACK_CENTER;
  const [pending, setPending] = useState<{ lat: number; lng: number } | null>(validLatLng(value));
  const [searchRows, setSearchRows] = useState<Array<{ lat: number; lon: number; label: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveRoddoDarkMode());
  const confirmed = validLatLng(value);

  useEffect(() => {
    const syncTheme = () => setIsDarkMode(resolveRoddoDarkMode());
    syncTheme();

    const observer = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => window.requestAnimationFrame(syncTheme))
      : null;

    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style'],
    });
    if (document.body) {
      observer?.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
    }

    window.addEventListener('rodo-theme-change', syncTheme as EventListener);
    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);

    return () => {
      observer?.disconnect();
      window.removeEventListener('rodo-theme-change', syncTheme as EventListener);
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
    };
  }, []);

  const upsertMarker = (lat: number, lng: number, isConfirmed = false) => {
    const map = mapRef.current;
    if (!map) return;
    if (!markerRef.current) {
      markerRef.current = (L as any).marker([lat, lng], {
        draggable: true,
        icon: createTournamentPinIcon(isConfirmed, isDarkMode),
        riseOnHover: true,
        zIndexOffset: 1000,
      }).addTo(map);
      markerRef.current.on('dragend', () => {
        const next = markerRef.current?.getLatLng?.();
        const nextLat = Number(next?.lat);
        const nextLng = Number(next?.lng);
        if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;
        setPending({ lat: nextLat, lng: nextLng });
        onChange(null);
        try { markerRef.current?.setIcon?.(createTournamentPinIcon(false, isDarkMode)); } catch {}
      });
    } else {
      markerRef.current.setLatLng([lat, lng]);
      try { markerRef.current.setIcon(createTournamentPinIcon(isConfirmed, isDarkMode)); } catch {}
    }
  };

  useEffect(() => {
    const host = mapHostRef.current;
    if (!host || mapRef.current) return;
    const map = (L as any).map(host, {
      center: [initial.lat, initial.lng],
      zoom: validLatLng(value) ? 14 : 8,
      zoomControl: true,
      attributionControl: true,
    });
    (L as any).tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors, Tiles style by Humanitarian OpenStreetMap Team hosted by OpenStreetMap France',
    }).addTo(map);
    mapRef.current = map;

    map.on('click', (event: any) => {
      const lat = Number(event?.latlng?.lat);
      const lng = Number(event?.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setPending({ lat, lng });
      onChange(null);
      upsertMarker(lat, lng, false);
    });

    const existing = validLatLng(value);
    if (existing) upsertMarker(existing.lat, existing.lng, true);
    const timer = window.setTimeout(() => { try { map.invalidateSize(false); } catch {} }, 80);
    return () => {
      window.clearTimeout(timer);
      try { map.remove(); } catch {}
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const tilePane = map.getPane?.('tilePane') as HTMLElement | undefined;
    if (!tilePane) return;

    tilePane.style.filter = isDarkMode
      ? 'brightness(0.50) contrast(0.96) saturate(0.72)'
      : 'none';
    tilePane.style.transition = 'filter 180ms ease';
  }, [isDarkMode]);

  useEffect(() => {
    const existing = validLatLng(value);
    if (!existing || !mapRef.current) return;
    setPending(existing);
    upsertMarker(existing.lat, existing.lng, true);
  }, [value?.lat, value?.lng]);

  const search = async () => {
    const q = locationLabel.trim();
    if (!q) {
      addToast('Enter a public location or water name first.', 'error');
      return;
    }
    setSearching(true);
    try {
      const rows = await forwardGeocodeServer(q, 5);
      setSearchRows(rows);
      if (!rows.length) addToast('No matching places found. Try a more specific location name.', 'info');
      else if (rows.length === 1) chooseSearchRow(rows[0]);
    } catch (error) {
      console.warn('[TournamentLocationPicker] search failed', error);
      addToast('Could not search that location right now.', 'error');
    } finally {
      setSearching(false);
    }
  };

  const chooseSearchRow = (row: { lat: number; lon: number; label: string }) => {
    setPending({ lat: row.lat, lng: row.lon });
    onChange(null);
    upsertMarker(row.lat, row.lon, false);
    mapRef.current?.setView?.([row.lat, row.lon], 14, { animate: true });
    setSearchRows([]);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      addToast('Location is not available on this device.', 'error');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude);
        const lng = Number(position.coords.longitude);
        setPending({ lat, lng });
        onChange(null);
        upsertMarker(lat, lng, false);
        mapRef.current?.setView?.([lat, lng], 15, { animate: true });
        setLocating(false);
      },
      () => {
        setLocating(false);
        addToast('Roddo could not access your current location.', 'error');
      },
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 60000 },
    );
  };

  const confirmPin = () => {
    if (!pending) {
      addToast('Tap the map or search for a place before confirming the tournament pin.', 'error');
      return;
    }
    const next: TournamentMapLocation = {
      lat: pending.lat,
      lng: pending.lng,
      source: 'manual',
      label: locationLabel.trim(),
    };
    onChange(next);
    upsertMarker(next.lat, next.lng, true);
    addToast('Big Blue tournament location confirmed.', 'success');
  };

  return (
    <div className="overflow-hidden rounded-[14px] border border-black/10 bg-white/35 dark:border-[rgba(159,140,220,.20)] dark:bg-[#151427]">
      <div className="space-y-2 px-3 py-2.5">
        <div>
          <div className="text-sm font-black text-slate-800 dark:text-slate-100">Map pin</div>
          <div className="mt-1 text-xs font-semibold opacity-65">Search or tap the map, then confirm the tournament area shown on Big Blue.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => void search()} disabled={searching} className="rounded-[12px] border border-[#2F80ED]/30 bg-[#2F80ED]/10 px-3 py-2 text-xs font-black text-[#2168C9] disabled:opacity-45 dark:border-[#7057E8]/30 dark:bg-[#7057E8]/[.16] dark:text-[#F4F6FA]">{searching ? 'Searching…' : 'Search location'}</button>
          <button type="button" onClick={useMyLocation} disabled={locating} className="rounded-[12px] border border-black/10 bg-white/70 px-3 py-2 text-xs font-black disabled:opacity-45 dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]">{locating ? 'Locating…' : 'Use my location'}</button>
        </div>
        {searchRows.length ? (
          <div className="max-h-36 space-y-1 overflow-y-auto rounded-[12px] border border-black/10 bg-white/80 p-1 dark:border-[rgba(159,140,220,.20)] dark:bg-[#151427]">
            {searchRows.map((row, index) => (
              <button key={`${row.lat}:${row.lon}:${index}`} type="button" onClick={() => chooseSearchRow(row)} className="block w-full rounded-[10px] px-2.5 py-2 text-left text-xs font-bold transition hover:bg-[#2F80ED]/10 dark:hover:bg-[#7057E8]/10">{row.label}</button>
            ))}
          </div>
        ) : null}
      </div>
      <div ref={mapHostRef} className="h-[205px] w-full bg-slate-200 dark:bg-[#1B1830]" aria-label="Select tournament location on map" />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-black/10 px-3 py-2.5 dark:border-[rgba(159,140,220,.20)]">
        <div className="min-w-0 text-xs font-semibold">
          {confirmed ? <span className="font-black text-emerald-700 dark:text-emerald-300">✓ Big Blue pin confirmed</span> : pending ? <span className="text-amber-700 dark:text-amber-300">Pin moved — confirm this location</span> : <span className="opacity-65">No tournament pin selected yet</span>}
        </div>
        <button type="button" onClick={confirmPin} disabled={!pending} className="rounded-[12px] border border-[#E5AA22] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_52%,#F6B936_100%)] px-3 py-2 text-xs font-black text-[#2F2410] shadow-[0_4px_12px_rgba(181,126,24,0.14)] disabled:opacity-45">Confirm pin</button>
      </div>
    </div>
  );
}
